/** DOM event names dispatched alongside WebSocket actions. Its own leaf: browser CustomEvent names are a different
 * vocabulary from the WS_ACTIONS protocol verbs, and their consumers (frontend shells, MFE hosts) are disjoint.
 *
 * WHAT THE SIBLING SUITE DOES NOT CHECK, stated here because the rules funnel every dispatcher and every listener onto
 * this record and a reader may reasonably take that funnelling for a wiring guarantee. It is not one:
 * `__tests__/websocket-actions-dom.test.ts` pins string SPELLINGS and value UNIQUENESS, and nothing anywhere asserts
 * that a member named in a dispatch is also named in a listener. Six members are dispatched today with NO listener
 * outside the test suite (measured 2026-09-03 over the whole tree, `addEventListener` + `useEventListener` + the
 * `utils/websocket/window-event-dispatch.ts` helper; the controls are AUCTION_EXTENDED and WISHLISTS_SYNCED, both of
 * which DO resolve to listeners, so the census can fire):
 *
 *   WISHLIST_DELETED, WISHLIST_UPDATED, WISHLIST_PARTICIPANT_ADDED, WISHLIST_PARTICIPANTS_UPDATED
 *     -- the per-wishlist deltas, dispatched by `components/lib/websocket/explore-client.wishlist-handlers.ts`.
 *   EXPLORE_WS_ERROR -- dispatched by `explore-client.ts` via `dispatchCriticalActionError`.
 *   FEDERA_SHELL_MISSING -- dispatched by `tools/mfe/auth-hooks-shim.ts` "for a host shell to surface in dev"; no host
 *     shell surfaces it.
 *
 * THE FOUR DELTAS ARE STRANDED, NOT REDUNDANT, and the difference decides whether they may be cut. WISHLISTS_SYNCED
 * looks like their supersession and structurally cannot be: it fires only on the SUCCESS path and carries the whole
 * list, while each delta carries a FAILURE ARM WITH AN ERROR STRING and per-wishlist roster identity. The wishlists MFE
 * moved to HTTP/react-query and took its error surfacing with it, so cutting the deltas loses information nothing else
 * carries. Whether a WebSocket-driven wishlist error surface returns is an OPERATOR question and is not answered here.
 *
 * A both-sides validator is what this gap actually owes and it cannot land yet: it would be red on day one against the
 * six above, and an allowlist to make it green would be a ratchet. The queue has to be adjudicated first. */

import { globalKey } from '@federa/const/global-namespace';

export const DOM_EVENTS = {
	AUCTION_EXTENDED: 'auctionExtended',
	WISHLIST_DELETED: 'wishlistDeleted',
	WISHLIST_UPDATED: 'wishlistUpdated',
	/** The explore socket delivered a fresh wishlists list; the document slot (`SHARED_WISHLISTS_KEY`) now holds it. */
	WISHLISTS_SYNCED: 'wishlistsSynced',
	WISHLIST_PARTICIPANT_ADDED: 'wishlistParticipantAdded',
	WISHLIST_PARTICIPANTS_UPDATED: 'wishlistParticipantsUpdated',
	EXPLORE_WS_STATE_CHANGE: 'exploreWebSocketStateChange',
	EXPLORE_WS_ERROR: 'exploreWebSocketError',
	MFE_URL_CHANGE: 'mfe-url-change',
	/** A dev tool asked the app to switch the signed-in role (`detail: { role?: string; userId?: string }`). Dev-only:
	 * `useDevRoleSwitch` gates its listener on an `isDev` callback, so the event is inert in production builds. */
	DEV_ROLE_SWITCH: 'dev-role-switch',
	FEDERA_AUTH_READY: 'federa-auth-ready',
	/** Firebase has RESOLVED auth state (a user or a confirmed signed-out), so `currentUser` is readable. Distinct from
	 * FEDERA_AUTH_READY, which announces the auth MFE's login-modal bundle: that says a UI is mountable, this says an
	 * identity is known. Latch + subscribe live in `utils/auth/firebase-auth-resolved.ts`. */
	FEDERA_FIREBASE_AUTH_RESOLVED: 'federa-firebase-auth-resolved',
	FEDERA_AUTH_LOGIN: 'federa-auth-login',
	/** An identity resolved on a page the single sign-on hub sent the tab back to, and the tab left carrying an INTENT
	 * (`SingleSignOnIntent`, `types/single-sign-on.ts`): a surface already mounted claims it now through
	 * `takeHubResumeIntent`; one that mounts later claims it on mount. Dispatched by `AuthProvider` beside
	 * FEDERA_AUTH_LOGIN, only when an intent is parked. */
	FEDERA_AUTH_RESUME: 'federa-auth-resume',
	FEDERA_SHELL_UPDATED: 'federa-shell-updated',
	/** One-shot MISCONFIGURATION signal: an MFE bundle mounted but found no shell bridge on `window`, so it is rendering
	 * a logged-out surface on what may be a live session. Dispatched by the `useAuth` shim
	 * (`tools/mfe/auth-hooks-shim.ts`) for a host shell to surface in dev. Its sibling above says the bridge CHANGED;
	 * this says the bridge is ABSENT. */
	FEDERA_SHELL_MISSING: 'federa-shell-missing',
	// Dispatched by the embedded chats/assistant MFE so the host shell can close the desktop assistant side panel.
	ASSISTANT_PANEL_CLOSE: 'federa-assistant-panel-close',
	// Broadcast by the host shell whenever the desktop assistant side panel opens/closes, so OTHER embedded MFEs in their
	// own React roots can react to the width change. `detail: { isOpen: boolean }`; the current value is also mirrored on
	// `window.__federaAssistantPanelOpen`.
	ASSISTANT_PANEL_STATE: 'federa-assistant-panel-state',
	/** The host saw INTENT on the Assistant control (pointer, focus) and mounted the panel hidden: the pane connects now,
	 *  so the socket is up by the click. Paired with the `AssistantWarm` window global for a pane that mounts after
	 *  the event fired (`utils/assistant/assistant-warm-signal.ts`). */
	ASSISTANT_PANEL_WARM: 'federa-assistant-panel-warm',
	CLEAR_GLOBAL_SEARCH: 'clearGlobalSearch',
	EXPLORE_SCROLL_RESET: 'exploreScrollReset',
	/** A keep-alive MFE host was RE-ATTACHED to the document (`detail: { keepAliveKey: string }`).
	 *
	 * Exists because keep-alive removes the signal a surface would normally restore on: the root never unmounts, so a
	 * mount-keyed `useEffect` does not re-run when the viewer returns and anything gated on mount silently stops firing.
	 * `useExploreScrollRestoration` is the founding consumer -- returning to /explore left the feed at the top because
	 * its restore effect saw an unchanged routeKey and propertiesLength and never ran. */
	MFE_HOST_REATTACHED: 'federa-mfe-host-reattached',
	/** A keep-alive MFE host was DETACHED from the document (`detail: { keepAliveKey: string }`).
	 *
	 * The counterpart to MFE_HOST_REATTACHED, and the one to use for "the viewer LEFT". Re-attach is NOT a proxy for
	 * "came back": `mountMFE` dispatches it whenever the host is not currently parented, and it is reached from the mount
	 * effect, the `mfe-<page>-ready` listener AND the resize/visibility sync -- so it fires on the OUTBOUND navigation too
	 * (measured: two REATTACH events per explore -> property -> explore round trip, one of them on the click). Detach
	 * happens exactly once, when the host is pulled out of the document. */
	MFE_HOST_DETACHED: 'federa-mfe-host-detached',
	EXPLORE_FILTER_NO_RESULTS: 'exploreFilterNoResults',
	EXPLORE_FILTER_APPLIED: 'exploreFilterApplied',
	LEDGER_UPDATED: 'ledgerUpdated',
	/** "The balance moved" -- re-broadcast by the explore client so the HOST does not need a second UserDO socket to hear
	 * it. Distinct from LEDGER_UPDATED on purpose, and by MEANING rather than by loop-avoidance: this one drives a
	 * scalar balance re-read, that one drives a transaction-LIST refetch. The explore client is the sole emitter of
	 * both -- no consumer relays one into the other. */
	BALANCE_SIGNAL: 'balanceSignal',
	/** The groups surface created a group (`detail: { groupId: string }`). A host nesting that surface -- the Connect
	 *  desk's first-group wizard -- re-reads the viewer's groups on it and leaves the wizard behind. */
	GROUP_CREATED: 'federa-group-created',
	/** The profile surface SAVED the viewer's profile (no detail). A host nesting that surface re-reads what it holds off
	 *  the same row in its own cache, which the surface's bundle cannot reach -- the Noor console's "Your page" address is
	 *  the username (`subscribeProfileUpdated`, `apps/noor/src/partners/use-services.ts`). */
	PROFILE_UPDATED: 'federa-profile-updated',
	/** The knowledgebase surface ADDED an entry from its setup view (no detail). The Connect desk nesting that view re-reads
	 *  whether its groups have answers every caller hears, so its setup checklist moves on (2026-09-28). */
	KNOWLEDGE_ADDED: 'federa-knowledge-added',
} as const;

/**
 * `window` key marking that Firebase has resolved auth state.
 *
 * On WINDOW rather than in module scope because each MFE/SPA is a SEPARATE BUNDLE with its own copy of any module: a
 * module-level latch set by the host shell reads false in the explore SPA's copy, so a consumer that subscribed after
 * the event fired would wait for one that already passed. The document is the only state both bundles share.
 */
export const FIREBASE_AUTH_RESOLVED_FLAG_KEY = globalKey('FirebaseAuthResolved');
